import { useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { renderClose } from "../../common/dialog";
import { ProductDto, ProjectDto, ProjectCycleDto, ProjectCycleEditDto } from "./common";
import { EnumItemDto, OfferTypeId, offerTypes } from "../../common/enum";
import { propBoolField, propCustomEnumField, propDateField, propNumberField, propSimpleEnumField } from "../../common/entityStoreForm";
import { createListStore, UseListStore } from "../../common/listStore";
import { max } from "lodash";
import { StoreWrapper } from "../../common/store";
import { combineLatest, map } from "rxjs";
import { useStateWithDeps } from "use-state-with-deps";
import { propMaxDateExclusive, propMinDateExclusive, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { NewMo } from "./ProjectCycleDialog.NewMo";
import { DefaultCycleId } from "../../common/defaultValues";
import { SaleArticleDto } from "../../common/order";

export function ProjectCycleDialog(props: {
    close: () => void;
    product: ProductDto;
    project: ProjectDto;
    projectCycle: ProjectCycleDto | null;
    projectCycleChanged(projectCycle: ProjectCycleDto): void;
    useProjectCycleListStore: UseListStore<ProjectCycleDto>;
    useSaleArticleListStore: UseListStore<SaleArticleDto>;
    hideSpecialOffer?: true;
}) {
    const [useProjectCycleStore] = useState(() => {
        const items = props.useProjectCycleListStore.getState().items.filter(p => p.cycleId !== DefaultCycleId);
        const index = props.projectCycle == null ? items.length : items.findIndex(i => i.cycleId === props.projectCycle!.cycleId);
        const startDateMin = index === 0 ? null : items[index - 1].startDate;
        const startDateMax = index >= items.length - 1 ? null : items[index + 1].startDate;
        const useStore = createEntityStore<ProjectCycleEditDto>({
            path: "products/projectCycles", qs: { projectId: `${props.project.id}` },
            saveProps: ["cycleId", "startDate", "offerType", "offerShipmentUnit", "offerProduct", "offerProject", "offerMailOrderShipmentId", "offerPrice",
                "critMaxActiveUse", "critMaxActiveValue",
                "critMinPayUse", "critMinPayVal",
                "critMaxPayUse", "critMaxPayVal",
                "critMaxOffUse", "critMaxOffVal", "critLastDaysVal",
                "critMaxSameUse", "critMaxSameVal", "critLastDaysSameVal",
            ],
            id: props.projectCycle != null ? props.projectCycle.id : null,
            getNewId: p => p.id!,
            data: props.projectCycle != null ? {
                ...props.projectCycle,
                startDateFormatted: undefined,
            } : newCycle(max(items.map(p => p.cycleId))),
            fetchOnInit: false,
            onSaved(p) {
                props.projectCycleChanged(p as ProjectCycleDto);
                props.close();
            },
            propValidators: [
                propValidValue("startDate", "startDateFormatted"),
                propRequired("startDate"),
                startDateMin != null && propMinDateExclusive("startDate", startDateMin),
                startDateMax != null && propMaxDateExclusive("startDate", startDateMax),

                propValueValidator("offerShipmentUnit", (p, e) => e.offerType === OfferTypeId.SuChange && !(p > 0) && "Required field."),
                propValueValidator("offerProject", (p, e) => e.offerType === OfferTypeId.NewCo && p == null && "Required field."),
                propValueValidator("offerMailOrderShipmentId", (p, e) => e.offerType === OfferTypeId.NewMo && p == null && "Required field."),
                propValueValidator("offerPrice", (p, e) => e.offerType !== OfferTypeId.None && !(p > 0) && "Value must be more than 0."),

                propValidValue("critMaxActiveValue", "critMaxActiveValueFormatted"),
                propRequired("critMaxActiveValue"),
                propMinValue("critMaxActiveValue", 0),

                propValidValue("critMinPayVal", "critMinPayValFormatted"),
                propRequired("critMinPayVal"),
                propMinValue("critMinPayVal", 0),

                propValidValue("critMaxPayVal", "critMaxPayValFormatted"),
                propRequired("critMaxPayVal"),
                propMinValue("critMaxPayVal", 0),

                propValidValue("critMaxOffVal", "critMaxOffValFormatted"),
                propRequired("critMaxOffVal"),
                propMinValue("critMaxOffVal", 0),
                propValidValue("critLastDaysVal", "critLastDaysValFormatted"),
                propRequired("critLastDaysVal"),
                propMinValue("critLastDaysVal", 0),

                propValidValue("critMaxSameVal", "critMaxSameValFormatted"),
                propRequired("critMaxSameVal"),
                propMinValue("critMaxSameVal", 0),
                propValidValue("critLastDaysSameVal", "critLastDaysSameValFormatted"),
                propRequired("critLastDaysSameVal"),
                propMinValue("critLastDaysSameVal", 0),
            ]
        });
        return useStore;
    });
    var projectCycleStore = useProjectCycleStore(p => ({
        save: p.save,
        isSaveDisabled: p.isSaveDisabled,
        entityId: p.entityId,
    }));

    return <>
        <DialogTitle>{projectCycleStore.entityId == null ? "New cycle" : "Edit cycle"}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={4} alignItems="stretch">
                <Grid item container columnSpacing={2} columns={props.hideSpecialOffer ? 6 : 12} xs={12} alignSelf="flex-start">
                    <Grid item xs={props.project.isDefault ? 6 : 3}><TextField label="Product" value={props.product.code + " " + props.product.name} disabled /></Grid>
                    {!props.project.isDefault && <Grid item xs={3}><TextField label="Project" value={props.project.code + " " + props.project.name} disabled /></Grid>}
                    <Grid item xs={3}>{propNumberField(useProjectCycleStore)("cycleId", null, "Cycle", { disabled: true })}</Grid>
                    <Grid item xs={3}>{propDateField(useProjectCycleStore)("startDate", "startDateFormatted", "Start date")}</Grid>
                </Grid>
                {!props.hideSpecialOffer && <SpecialOffer useProjectCycleStore={useProjectCycleStore} useSaleArticleListStore={props.useSaleArticleListStore} />}
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={projectCycleStore.save} disabled={projectCycleStore.isSaveDisabled} color="save">Save</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}

function SpecialOffer({ useProjectCycleStore, useSaleArticleListStore }: {
    useProjectCycleStore: UseEntityStore<ProjectCycleEditDto>;
    useSaleArticleListStore: UseListStore<SaleArticleDto>;
}) {
    function resetOfferType(offerType: OfferTypeId, oldOfferType: OfferTypeId) {
        if (oldOfferType === OfferTypeId.SuChange) {
            useProjectCycleStore.getState().setClientDataProp("offerShipmentUnit", 0);
        } else if (oldOfferType === OfferTypeId.NewCo) {
            useProjectCycleStore.getState().setClientDataProp("offerProduct", null);
            useProjectCycleStore.getState().setClientDataProp("offerProject", null);
        } else if (oldOfferType === OfferTypeId.NewMo) {
            useProjectCycleStore.getState().setClientDataProp("offerMailOrderShipmentId", null);
        }

        if (offerType === OfferTypeId.None) {
            useProjectCycleStore.getState().setClientDataProp("offerPrice", 0);
            useProjectCycleStore.getState().setClientDataProp("offerPriceFormatted", undefined);
        }
    }

    return <>
        <Grid item container columnSpacing={2} xs={6} alignSelf="flex-start">
            <Grid item xs={12}><Typography variant="h6">Special offer</Typography></Grid>
            <Grid item xs={12}>{propSimpleEnumField(useProjectCycleStore)("offerType", "Offer type", offerTypes, {
                onChange: (newValue, oldValue) => resetOfferType(newValue, oldValue)
            })}</Grid>
            <StoreWrapper useStore={() => useProjectCycleStore} render={useProjectCycleStore => {
                const offerType = useProjectCycleStore(p => p.clientData.offerType);
                if (offerType === OfferTypeId.SuChange) {
                    return <>
                        <Grid item xs={12}>{propSimpleEnumField(useProjectCycleStore)("offerShipmentUnit", "Offer SU", offerSuOptions)}</Grid>
                        <Grid item xs={12}>{propNumberField(useProjectCycleStore)("offerPrice", "offerPriceFormatted", "Offer price", { isCurrency: true })}</Grid>
                    </>
                } else if (offerType === OfferTypeId.NewCo) {
                    return <NewCo useProjectCycleStore={useProjectCycleStore} />;
                } else if (offerType === OfferTypeId.NewMo) {
                    return <NewMo useProjectCycleStore={useProjectCycleStore} useSaleArticleListStore={useSaleArticleListStore} />;
                } else {
                    return null;
                }
            }} />
        </Grid>
        <Grid item container columnSpacing={2} xs alignSelf="flex-start">
            <Grid item xs={12}><Typography variant="h6">Offer criteria</Typography></Grid>
            <Grid item xs={5}></Grid>
            <Grid item xs={5}>{propNumberField(useProjectCycleStore)("critMaxActiveValue", "critMaxActiveValueFormatted", "Maximum number of active orders")}</Grid>
            <Grid item xs={2}>{propBoolField(useProjectCycleStore)("critMaxActiveUse", "Use")}</Grid>

            <Grid item xs={5}></Grid>
            <Grid item xs={5}>{propNumberField(useProjectCycleStore)("critMinPayVal", "critMinPayValFormatted", "Minimum paid amount")}</Grid>
            <Grid item xs={2}>{propBoolField(useProjectCycleStore)("critMinPayUse", "Use")}</Grid>

            <Grid item xs={5}></Grid>
            <Grid item xs={5}>{propNumberField(useProjectCycleStore)("critMaxPayVal", "critMaxPayValFormatted", "Maximum paid amount")}</Grid>
            <Grid item xs={2}>{propBoolField(useProjectCycleStore)("critMaxPayUse", "Use")}</Grid>

            <Grid item xs={5}>{propNumberField(useProjectCycleStore)("critMaxOffVal", "critMaxOffValFormatted", "Maximum number of offers")}</Grid>
            <Grid item xs={5}>{propNumberField(useProjectCycleStore)("critLastDaysVal", "critLastDaysValFormatted", "for last", { unit: "days" })}</Grid>
            <Grid item xs={2}>{propBoolField(useProjectCycleStore)("critMaxOffUse", "Use")}</Grid>

            <Grid item xs={5}>{propNumberField(useProjectCycleStore)("critMaxSameVal", "critMaxSameValFormatted", "Maximum number of same offers")}</Grid>
            <Grid item xs={5}>{propNumberField(useProjectCycleStore)("critLastDaysSameVal", "critLastDaysSameValFormatted", "for last", { unit: "days" })}</Grid>
            <Grid item xs={2}>{propBoolField(useProjectCycleStore)("critMaxSameUse", "Use")}</Grid>
        </Grid>
    </>;
}

function NewCo(props: {
    useProjectCycleStore: UseEntityStore<ProjectCycleEditDto>;
}) {
    const [{ useProductsStore, useProjectsStore }] = useStateWithDeps(() => {
        const useProductsStore = createListStore<ProductListItemDto>({ path: "products/projectCycles/productsAndProjects" });
        const useProjectsStore = createListStore<ProjectListItemDto>({
            data: combineLatest([useProductsStore.getState().items$, props.useProjectCycleStore.getState().clientData$]).pipe(map(([products, cycle]) => ({
                items: products.items.find(p => p.id === cycle.data.offerProduct)?.projects || [],
                isFetching: products.isFetching
            })))
        });
        return { useProductsStore, useProjectsStore };
    }, [props.useProjectCycleStore]);
    return <>
        <Grid item xs={12}>{propCustomEnumField(props.useProjectCycleStore)("offerProduct", "Offer product", useProductsStore, {
            getId: p => p.id, getName: p => `${p.code} ${p.name}`, onChange: () => {
                props.useProjectCycleStore.getState().setClientDataProp("offerProject", null);
            }
        })}</Grid>
        <Grid item xs={12}>{propCustomEnumField(props.useProjectCycleStore)("offerProject", "Offer project", useProjectsStore,
            { getId: p => p.id, getName: p => `${p.code} ${p.name}` })}</Grid>
        <Grid item xs={12}>{propNumberField(props.useProjectCycleStore)("offerPrice", "offerPriceFormatted", "Offer price", { isCurrency: true })}</Grid>
    </>;
}

const newCycle: (maxCycleId: number | undefined) => ProjectCycleEditDto = maxCycleId => ({
    id: null,
    cycleId: maxCycleId == null ? 1 : maxCycleId + 1,
    startDate: "",
    offerType: OfferTypeId.None,
    offerShipmentUnit: 0,
    offerProduct: null,
    offerProject: null,
    offerPrice: 0,
    offerMailOrderShipmentId: null,
    critMaxActiveUse: false,
    critMaxActiveValue: 0,
    critMinPayUse: false,
    critMinPayVal: 0,
    critMaxPayUse: false,
    critMaxPayVal: 0,
    critMaxOffUse: false,
    critMaxOffVal: 0,
    critLastDaysVal: 0,
    critMaxSameUse: false,
    critMaxSameVal: 0,
    critLastDaysSameVal: 0,
});

interface ProductListItemDto {
    id: number;
    code: string;
    name: string;
    projects: ProjectListItemDto[];
}

interface ProjectListItemDto {
    id: number;
    code: string;
    name: string;
}

const offerSuOptions: EnumItemDto[] = [
    { id: 2, name: "2" },
    { id: 3, name: "3" },
];
